const MB = 1024 * 1024;
const PDF = { ext: ['pdf'], mime: ['application/pdf'] };
const PDF_IMG = {
    ext: ['pdf', 'jpg', 'jpeg', 'png'],
    mime: ['application/pdf', 'image/jpeg', 'image/png'],
};
const PDF_IMG_OFFICE = {
    ext: ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'tif', 'tiff', 'xlsx', 'docx'],
    mime: [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/heic',
        'image/tiff',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
};
function def(code, label, entityType, opts = {}) {
    const formats = opts.formats ?? PDF;
    return {
        code,
        label,
        entityType,
        sensitivity: opts.sensitivity ?? 'internal',
        allowedExt: formats.ext,
        allowedMime: formats.mime,
        maxBytes: (opts.maxMb ?? 50) * MB,
        compress: opts.compress ?? true,
        immutable: opts.immutable ?? false,
        retentionYears: opts.retentionYears === undefined ? 7 : opts.retentionYears,
        retentionTrigger: opts.retentionTrigger ?? 'upload',
    };
}
/**
 * Policy is data. These are the seed values for the `document_type` table (spec §4, §6, §8);
 * Records.Admin can edit limits in the admin UI. Retention periods are starting points pending counsel.
 */
export const DOCUMENT_TYPES = [
    // ── Customer ──
    def('CREDIT_APP', 'Credit application', 'customer', {
        sensitivity: 'restricted', immutable: true, retentionTrigger: 'account_inactivated',
    }),
    def('PERS_GUAR', 'Personal guarantee', 'customer', {
        sensitivity: 'restricted', immutable: true, retentionTrigger: 'account_inactivated',
    }),
    def('FINANCIAL', 'Financial statement', 'customer', {
        sensitivity: 'restricted', retentionTrigger: 'account_inactivated',
    }),
    def('RESALE_CERT', 'Resale certificate', 'customer', {
        formats: PDF_IMG, maxMb: 25, retentionYears: 4, retentionTrigger: 'superseded',
    }),
    def('W9', 'W-9', 'customer', {
        formats: PDF_IMG, maxMb: 25, retentionYears: 4, retentionTrigger: 'last_1099',
    }),
    def('LEGAL', 'Legal document', 'customer', { immutable: true, retentionTrigger: 'account_inactivated' }),
    def('CUSTOMER_OTHER', 'Other customer document', 'customer', {
        formats: PDF_IMG_OFFICE, maxMb: 100, retentionTrigger: 'account_inactivated',
    }),
    // ── Job (customer site) ──
    def('PRELIM', 'Preliminary notice', 'site', { immutable: true, retentionTrigger: 'job_closed' }),
    def('LIEN_REL_COND', 'Conditional lien release', 'site', { immutable: true, retentionTrigger: 'job_closed' }),
    def('LIEN_REL_UNCOND', 'Unconditional lien release', 'site', { immutable: true, retentionTrigger: 'job_closed' }),
    def('JOB_INFO', 'Job information sheet', 'site', { retentionTrigger: 'job_closed' }),
    def('BID_REQ', 'Bid requirement', 'site', { formats: PDF_IMG_OFFICE, maxMb: 100, retentionTrigger: 'job_closed' }),
    def('JOB_DOC', 'Job document', 'site', {
        formats: PDF_IMG_OFFICE, maxMb: 100, retentionTrigger: 'job_closed',
    }),
    // ── Supplier ──
    def('SUPPLIER_W9', 'Supplier W-9', 'supplier', { retentionYears: 4, retentionTrigger: 'last_1099' }),
    def('SUPPLIER_TIN', 'TIN match result', 'supplier', { retentionYears: 4, retentionTrigger: 'last_1099' }),
    def('SUPPLIER_BANK', 'ACH / EFT banking update', 'supplier', {
        sensitivity: 'restricted', retentionYears: 6, retentionTrigger: 'superseded',
    }),
    def('SUPPLIER_INS', 'Certificate of insurance', 'supplier', { retentionYears: 6, retentionTrigger: 'term_end' }),
    def('SUPPLIER_CONTRACT', 'Supplier contract', 'supplier', { retentionYears: 6, retentionTrigger: 'term_end' }),
    def('SUPPLIER_ARCHIVE', 'Superseded supplier document', 'supplier', { retentionYears: 6, retentionTrigger: 'superseded' }),
    def('SUPPLIER_OTHER', 'Other supplier document', 'supplier', {
        formats: PDF_IMG_OFFICE, maxMb: 100, retentionYears: 6, retentionTrigger: 'superseded',
    }),
];
export const DOCUMENT_TYPE_BY_CODE = new Map(DOCUMENT_TYPES.map((d) => [d.code, d]));
export function getDocumentType(code) {
    const dt = DOCUMENT_TYPE_BY_CODE.get(code);
    if (!dt)
        throw new Error(`Unknown document type: ${code}`);
    return dt;
}
/** Extension check that survives names like "credit app.pdf (2)" (BisTrack has seven of those). */
export function extensionOf(filename) {
    const cleaned = filename.replace(/\s*\(\d+\)\s*$/, '');
    const idx = cleaned.lastIndexOf('.');
    return idx >= 0 ? cleaned.slice(idx + 1).toLowerCase() : '';
}
export function isExtensionAllowed(dt, filename) {
    return dt.allowedExt.includes(extensionOf(filename));
}
//# sourceMappingURL=document-types.js.map